/**
 * Formatting helpers — HTML escape, duration, time, and byte display.
 * Moved from gallery/format.ts during gallery retirement.
 * Consolidated from api-client.ts, guidance-types.ts, auth/core.ts, message-handler.ts, share.ts.
 */

// --- HTML escaping ---

/** Escape a string for safe interpolation into HTML text content. Covers all 5 dangerous chars. */
export function escHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/** Legacy alias used by guidance-types.ts barrel re-export and many consumers. */
export const esc = escHtml;

// --- Duration formatting ---

export function fmtDur(ms: number): string {
  if (!ms) return "--";
  const s = Math.floor(ms / 1000);
  if (s < 60) return s + "s";
  const m = Math.floor(s / 60);
  if (m < 60) return m + "m " + (s % 60) + "s";
  return Math.floor(m / 60) + "h " + (m % 60) + "m";
}

/** Format uptime from milliseconds (guidance panel, telemetry). */
export function formatUptimeMs(ms: number): string {
  if (!ms || ms <= 0) return "--";
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${s % 60}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}

/** Format uptime from seconds (stats display). */
export function formatUptime(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ${m % 60}m`;
  const d = Math.floor(h / 24);
  return `${d}d ${h % 24}h`;
}

// --- Time formatting ---

export function fmtTime(iso: string): string {
  if (!iso) return "--";
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" }) +
    " " + d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
}

/** Format ISO timestamp to locale time only. */
export function formatTime(iso?: string): string {
  if (!iso) return "--";
  try {
    return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch { return "--"; }
}

/** Format ISO timestamp to locale date + time. */
export function formatDateTime(iso?: string): string {
  if (!iso) return "--";
  try {
    return new Date(iso).toLocaleDateString([], { month: "short", day: "numeric" }) +
      " " + new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch { return "--"; }
}

// --- Byte formatting ---

/** Format bytes to human readable string. */
export function formatBytes(bytes: number): string {
  const mb = bytes / (1024 * 1024);
  if (mb > 1024) return `${(mb / 1024).toFixed(1)} GB`;
  if (mb > 1) return `${mb.toFixed(0)} MB`;
  return `${(bytes / 1024).toFixed(0)} KB`;
}
